import React, { useState, useEffect } from 'react';
import { Card, Button, Spinner, Badge } from 'react-bootstrap';
import { Link, useNavigate } from 'react-router-dom';
import AdvanceTableProvider from 'providers/AdvanceTableProvider';
import AdvanceTable from 'components/common/advance-table/AdvanceTable';
import useAdvanceTable from 'hooks/useAdvanceTable';
import AdvanceTableFooter from 'components/common/advance-table/AdvanceTableFooter_paging';
import AdvanceTablePagination from 'components/common/advance-table/AdvanceTablePagination_paging';
import CheckOutModal from 'components/modal/CheckOutModal';
import { useAppContext } from 'providers/AppProvider';
import { fetchPricingPolicies } from 'hooks/CallApi';
import { formatDate } from 'components/common/SunnyUtils';
import styled from 'styled-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faFileInvoiceDollar } from '@fortawesome/free-solid-svg-icons';

// --- Styled Components ---
const PageContainer = styled.div`
  padding: 2rem 1rem;
  max-width: 1400px;
  margin: 0 auto;
`;

const HeaderSection = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  margin-bottom: 2rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid #e2e8f0;
`;

const TitleWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const IconWrapper = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 12px;
  background: linear-gradient(135deg, #e0e7ff 0%, #c3d4fe 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #3b82f6;
  font-size: 1.5rem;
  box-shadow: 0 4px 6px rgba(59, 130, 246, 0.1);
`;

const PageTitle = styled.h2`
  margin: 0;
  font-weight: 700;
  color: #1e293b;
  font-size: 1.75rem;
  letter-spacing: -0.5px;
`;

const PageSubtitle = styled.p`
  margin: 4px 0 0 0;
  color: #64748b;
  font-size: 0.95rem;
`;

const StyledCard = styled(Card)`
  border: none;
  border-radius: 16px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.04), 0 1px 3px rgba(0, 0, 0, 0.02);
  overflow: hidden;
  transition: box-shadow 0.3s ease;
  
  &:hover {
    box-shadow: 0 10px 25px rgba(0, 0, 0, 0.08);
  }
`;

const ActionButton = styled(Button)`
  border-radius: 8px;
  font-weight: 600;
  padding: 0.6rem 1.2rem;
  display: flex;
  align-items: center;
  gap: 8px;
  box-shadow: 0 2px 4px rgba(59, 130, 246, 0.2);
  transition: all 0.2s ease;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 8px rgba(59, 130, 246, 0.3);
  }
`;

const ToolbarContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.25rem 1.5rem;
  background-color: #f8fafc;
  border-bottom: 1px solid #f1f5f9;
`;

const VersionBadge = styled(Badge)`
  background-color: #f1f5f9 !important;
  color: #475569 !important;
  border: 1px solid #cbd5e1;
  font-weight: 600;
  padding: 0.4rem 0.6rem;
  border-radius: 6px;
`;

const DescriptionText = styled.span`
  color: #475569;
  display: inline-block;
  max-width: 300px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const DateText = styled.span`
  color: #64748b;
  font-size: 0.9rem;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 6px;
`;

function AdvanceTableExample() {
  const navigate = useNavigate();
  const [tableData, setTableData] = useState([]);
  const [totalElements, setTotalElements] = useState(0);
  const [loading, setLoading] = useState(true);
  const [showSpinner, setShowSpinner] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [perPage, setPerPage] = useState(10);

  const [ErrorCode, setErrorCode] = useState('');
  const [ErrorMsg, setErrorMsg] = useState('');
  const [modalErrorShow, setModalErrorShow] = useState(false);

  const {
    config: { isNavbarVerticalCollapsed },
    setConfig
  } = useAppContext();

  const columns = [
    {
      accessorKey: 'pricingPolicyName',
      header: '정책명',
      meta: { 
        headerProps: { className: 'text-900 fw-bold bg-light py-3 fs-0' },
        cellProps: { className: 'fw-semibold text-primary fs-0' }
      },
      enableSorting: false,
    },
    {
      accessorKey: 'description',
      header: '설명',
      meta: { headerProps: { className: 'text-900 fw-bold bg-light py-3 fs-0' } },
      enableSorting: false,
      cell: ({ row }) => <DescriptionText>{row.original.description || '-'}</DescriptionText>
    },
    {
      accessorKey: 'version',
      header: '버전',
      meta: { headerProps: { className: 'text-900 fw-bold bg-light py-3 text-center fs-0', style: { width: '100px' } }, cellProps: { className: 'text-center' } },
      enableSorting: false,
      cell: ({ row }) => <VersionBadge>{row.original.version}</VersionBadge>
    },
    {
      accessorKey: 'createdAt',
      header: '등록일시',
      meta: { headerProps: { className: 'text-900 fw-bold bg-light py-3 text-end fs-0', style: { width: '200px' } }, cellProps: { className: 'text-end' } },
      enableSorting: false,
      cell: ({ row }) => (
        <DateText>
          <i className="far fa-calendar-alt text-400"></i>
          {row.original.createdAt}
        </DateText>
      )
    }
  ];

  const table = useAdvanceTable({
    data: tableData,
    columns,
    sortable: true,
    selection: false,
    pagination: true,
    perPage
  });

  const loadData = async (page, size) => {
    const spinnerTimer = setTimeout(() => setShowSpinner(true), 300);
    setLoading(true);

    try {
      const response = await fetchPricingPolicies(page, size);

      if (response && response.data) {
        const { content, page: pageInfo } = response.data;
        
        const formattedData = content.map(policy => ({
          ...policy,
          createdAt: policy.insertTimeUtc ? formatDate(policy.insertTimeUtc) : '정보 없음',
          version: policy.version || '-',
          pricingPolicyUuid: policy.pricingPolicyUuid
        }));
        
        setTableData(formattedData);
        setTotalElements(pageInfo.totalElements);
      } else {
        throw new Error('Invalid data format received');
      }
    } catch (error) {
      console.error('데이터 로드 에러:', error);
      setErrorMsg(error.response?.data?.message || '데이터를 불러오는 중 오류가 발생했습니다.');
      setModalErrorShow(true);
      setTableData([]);
      setTotalElements(0);
    } finally {
      clearTimeout(spinnerTimer);
      setLoading(false);
      setShowSpinner(false);
    }
  };

  useEffect(() => {
    setConfig('isNavbarVerticalCollapsed', true);
    loadData(currentPage, perPage);
  }, [currentPage, perPage]);

  const handlePageChange = newPage => setCurrentPage(newPage);
  
  const handlePerPageChange = newPerPage => {
    setPerPage(newPerPage);
    setCurrentPage(0);
  };

  const handleRowClick = row => {
    navigate(`/safedb/SA/pricingPolicy/detail/${row.original.pricingPolicyUuid}`);
  };

  return (
    <AdvanceTableProvider {...table}>
      <StyledCard>
        <ToolbarContainer>
          <AdvanceTableFooter
            rowsPerPageSelection
            onPageChange={(page, newPageSize) => {
              handlePerPageChange(newPageSize);
              setCurrentPage(page);
            }}
          />
          <Link to="/safedb/SA/pricingPolicy/add" style={{ textDecoration: 'none' }}>
            <ActionButton variant="primary">
              <FontAwesomeIcon icon={faPlus} />
              새 정책 등록
            </ActionButton>
          </Link>
        </ToolbarContainer>

        {loading ? (
          <div className="d-flex justify-content-center align-items-center py-5" style={{ minHeight: '300px' }}>
            {showSpinner && (
              <Spinner animation="border" variant="primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </Spinner>
            )}
          </div>
        ) : tableData.length === 0 ? (
          <div className="text-center py-5">
            <div className="mb-3 text-400">
              <FontAwesomeIcon icon={faFileInvoiceDollar} size="3x" />
            </div>
            <h5 className="text-600 fw-normal">등록된 요금정책이 없습니다.</h5>
            <p className="text-500 mb-4">새로운 요금정책을 등록하여 서비스를 구성해보세요.</p>
          </div>
        ) : (
          <div className="table-responsive">
            <AdvanceTable
              headerClassName="bg-light text-nowrap align-middle border-bottom border-200"
              rowClassName="align-middle white-space-nowrap cursor-pointer hover-bg-light transition-base"
              tableProps={{
                className: 'fs--1 mb-0 border-0',
                hover: true
              }}
              onRowClick={handleRowClick}
            />
          </div>
        )}
        
        <Card.Footer className="bg-light py-3 border-top">
          <div className="fs--1 fw-semi-bold text-500 mb-2">
            총 <span className="text-primary fw-bold">{totalElements.toLocaleString()}</span>건의 정책
          </div>
          <div className="d-flex justify-content-center">
            <AdvanceTablePagination
              currentPage={currentPage}
              totalItems={totalElements}
              itemsPerPage={perPage}
              onChange={newPage => handlePageChange(newPage)}
            />
          </div>
        </Card.Footer>
      </StyledCard>

      <CheckOutModal
        show={modalErrorShow}
        onHide={() => setModalErrorShow(false)}
        onConfirm={() => setModalErrorShow(false)}
        title="경고"
        body={`${ErrorMsg}`}
        confirmText="확인"
        variant="warning"
        SubmitButton={true}
      />
    </AdvanceTableProvider>
  );
}

const PricingPolicy = () => {
  return (
    <PageContainer>
      <HeaderSection>
        <TitleWrapper>
          <IconWrapper>
            <FontAwesomeIcon icon={faFileInvoiceDollar} />
          </IconWrapper>
          <div>
            <PageTitle>요금정책 관리</PageTitle>
            <PageSubtitle>고객사에 제공할 서비스 항목과 가격 정책을 관리합니다.</PageSubtitle>
          </div>
        </TitleWrapper>
      </HeaderSection>
      
      <AdvanceTableExample />
    </PageContainer>
  );
};

export default PricingPolicy;
